import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { fetchSearchListItem, fetchOtherListings, fetchSearchList } from '../data/api/search';
import { googleEvent, shuffleArray, changeTitle } from '../utils/dom';
import { useRouterContext } from './RouterProvider';
import { ReportListingResponseSchema } from '@/schemas/api/listings';
import { trackError, ErrorSeverity } from '../utils/error-tracking';
import { isBackNavigation } from '../utils/navigation-direction';
import { getProductPath as productPathFor } from '../utils/product/product-path';

/**
 * Slim CJ Dropshipping variant shape used by the product page picker.
 * Mirrors `utils/cj-variants.ts:ProductVariant`. Kept duplicated here so
 * the context module doesn't pull in a server-only file (the helper imports
 * `prisma` indirectly via its sibling modules in some test paths).
 */
export interface ProductVariant {
  vid: string;
  name: string;
  price: number;
  image: string | null;
  key: string;
  standard: string;
}

export interface Product {
  id: string;
  title: string;
  price: number;
  description?: string;
  photos?: Record<string, unknown>[];
  user: {
    id: string;
    name?: string;
  };
  categoryId?: string;
  categoryGroupId?: number;
  latitude?: number;
  longitude?: number;
  locationText?: string;
  condition?: string;
  brand?: string;
  shippable?: boolean;
  tags?: string[];
  dataSourceId?: number;
  remote_url?: string;
  /**
   * Listing status (see data/js/listing-statuses). Declared as number|string
   * because that is what actually arrives: the listing API returns a number and
   * the edit form round-trips it as a string, which is why every reader in
   * useProductDetailState compares against both forms.
   */
  status?: number | string;
  /**
   * Set while the listing is featured. Read only by the admin right-click menu
   * on the main photo, which uses it to offer Feature or Refeature.
   */
  featuredDate?: string | Date | null;
  /**
   * CJ Dropshipping variant catalog. Only present for CJ listings with
   * 2+ usable variants — the listing fetch (`pages/api/listings/[id].ts`)
   * omits the field for everything else, so its presence is the signal to
   * render the variant picker.
   */
  variants?: ProductVariant[];
  /**
   * The stored price analysis, parsed (`pages/api/listings/[id].ts`). Read by
   * the Deal Score callout to decide whether to ask the server about it;
   * the server re-reads the row before anything is shown.
   */
  aiPriceAnalysis?: unknown;
  /**
   * Live listings this seller has of this same item, this one included. Sent by
   * the search APIs; the listing cards show "N copies" when it is 2 or more.
   */
  copies?: number;
}

export interface ReplaceListSearchParams {
  page?: number;
  categoryGroupId?: string | number;
  categoryId?: string | number;
  catId?: string | number;
}

export interface ProductContextType {
  productDetail: Product | null;
  currentList: Product[];
  recommendedList: Product[];
  otherList: Product[];
  similarList: Product[];
  otherListLoaded: boolean;
  currentListGroup: string;
  currentListIndex: number;
  coverPosition: number;
  loaded: boolean;
  /**
   * Currently selected CJ variant id. `null` for non-variant listings or
   * before the productDetail's variant default has been computed.
   */
  selectedVariantId: string | null;
  /**
   * Derived: looks up `selectedVariantId` in `productDetail.variants`.
   * Returns `null` for non-variant listings.
   */
  selectedVariant: ProductVariant | null;
  setSelectedVariantId: (vid: string | null) => void;

  setProduct: (product: Product, listGroup?: string, isBack?: boolean) => void;
  removeProduct: () => void;
  applyProductStatus: (listingId: string | number, status: number) => void;
  prevProduct: (e?: React.MouseEvent) => void;
  nextProduct: (e?: React.MouseEvent) => void;
  flipProduct: (adjustment: number) => void;
  getSingleProduct: (id: string) => Promise<Product | null>;
  loadFullProductDetails: (product: Product) => Promise<void>;
  loadOtherListings: () => Promise<void>;
  reportProduct: (listing: Product, flag: string | number, reason: string) => Promise<boolean>;
  getProductPath: (product: Product) => string;
  setCoverPos: (position: number) => void;
  setLoaded: (loaded: boolean) => void;
  setUserListings: (listings: Product[]) => void;
  setSimilarListings: (listings: Product[]) => void;
  replaceCurrentList: (list: Product[], searchParams?: ReplaceListSearchParams) => void;
  chooseRecommendedFromList: (productId: string, list: Product[]) => Product[];
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProduct = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
};

/**
 * Non-throwing variant of useProduct for consumers that may be mounted
 * outside a ProductProvider tree (e.g. CheckoutProvider, which is used in
 * standalone embed flows and unit tests that don't need product state).
 * Returns `null` instead of throwing when there's no provider.
 */
export const useProductOptional = (): ProductContextType | null => {
  const context = useContext(ProductContext);
  return context ?? null;
};

interface ProductProviderProps {
  children: ReactNode;
  initialProductDetail?: Product;
  initialCurrentList?: Product[];
  initialRecommendedList?: Product[];
  initialOtherList?: Product[];
}

const RECOMMENDED_COUNT = 8;
const MAX_LIST_SIZE = 32;

const loadingList: Product[] = new Array(10).fill({
  id: '',
  title: '',
  price: 0,
  user: { id: '' },
});

// Static empty arrays to avoid creating new references on every render
const EMPTY_ARRAY: Product[] = [];

/**
 * Put the listing we just moved to at the top of whatever is actually scrolling.
 *
 * Two different elements scroll a product view, and only one of them was ever
 * reset here:
 *
 * - On a `/product/...` page the DOCUMENT scrolls. `.product-overlay` is not
 *   rendered at all there (all-layout mounts it only when a product is open
 *   while the URL is NOT a product route), so resetting its scrollTop did
 *   nothing and the flip buttons left the reader wherever they had been --
 *   typically halfway down the previous listing's description, now looking at
 *   the middle of the next one. Next does not cover this either: the flip moves
 *   the URL with `shallow: true`, and the pages router computes
 *   `shouldScroll = options.scroll ?? !isValidShallowRoute`, so a same-route
 *   shallow push is deliberately exempt from its scroll reset.
 * - In the overlay (a product opened over a browse page) `.product-overlay` is
 *   `position: fixed` with its own `overflow-y: scroll`, so IT is the scroller
 *   and the window scroll belongs to the browse page underneath. Scrolling the
 *   window there would throw away the browse position the reader comes back to,
 *   which is why the document is only reset when no overlay is mounted.
 */
const scrollProductViewToTop = () => {
  // Only the flip buttons call this, so it always runs in the browser.
  const overlay = document.querySelector('.product-overlay') as HTMLElement | null;
  const modal = document.querySelector('.product-modal') as HTMLElement | null;

  if (overlay) overlay.scrollTop = 0;
  if (modal) modal.scrollTop = 0;

  if (!overlay) {
    window.scrollTo(0, 0);
  }
};

export const ProductProvider: React.FC<ProductProviderProps> = ({
  children,
  initialProductDetail = null,
  initialCurrentList = EMPTY_ARRAY,
  initialRecommendedList = EMPTY_ARRAY,
  initialOtherList = EMPTY_ARRAY,
}) => {
  // Use SSG-safe router context
  const { push } = useRouterContext();

  const [productDetail, setProductDetail] = useState<Product | null>(initialProductDetail);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [currentList, setCurrentList] = useState<Product[]>(initialCurrentList);
  const [recommendedList, setRecommendedList] = useState<Product[]>(initialRecommendedList);
  const [otherList, setOtherList] = useState<Product[]>(initialOtherList);
  const [similarList, setSimilarList] = useState<Product[]>(EMPTY_ARRAY);
  const [otherListLoaded, setOtherListLoaded] = useState(false);
  const [currentListGroup, setCurrentListGroup] = useState('currentList');
  const [currentListIndex, setCurrentListIndex] = useState(0);
  const [coverPosition, setCoverPosition] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [shouldLoadDetails, setShouldLoadDetails] = useState<Product | null>(null);
  const lastCategoryIdRef = useRef<string | number | null>(null);
  const prevInitialListRef = useRef<Product[]>(initialCurrentList);

  // Refs for state that is both read and written inside the same useCallback,
  // allowing those callbacks to have stable identities (no-unstable-context-callback).
  const otherListLoadedRef = useRef(false);
  const currentListRef = useRef<Product[]>(initialCurrentList);
  const recommendedListRef = useRef<Product[]>(initialRecommendedList);
  const otherListRef = useRef<Product[]>(initialOtherList);
  const similarListRef = useRef<Product[]>(EMPTY_ARRAY);
  const currentListGroupRef = useRef('currentList');
  const currentListIndexRef = useRef(0);

  // Keep refs in sync with state DURING RENDER. These refs are read synchronously by
  // same-render guards (e.g. loadOtherListings checks otherListLoadedRef.current to avoid
  // a duplicate fetch), so they MUST be updated in render — moving them into a post-commit
  // effect makes those guards read stale values and causes duplicate API calls.
  /* eslint-disable react-hooks/refs -- intentional synchronous state->ref mirror read by same-render guards; updating in an effect breaks the guards (see comment above) */
  otherListLoadedRef.current = otherListLoaded;
  currentListRef.current = currentList;
  recommendedListRef.current = recommendedList;
  otherListRef.current = otherList;
  similarListRef.current = similarList;
  currentListGroupRef.current = currentListGroup;
  currentListIndexRef.current = currentListIndex;
  /* eslint-enable react-hooks/refs */

  /**
   * Default `selectedVariantId` to the cheapest variant whenever the
   * productDetail (or its variant catalog) changes. Picking the cheapest
   * matches the server-side fallback in `pages/api/checkout/create-payment-intent.ts`
   * — so the price the buyer sees on first render is what they'll be
   * charged if they don't touch the picker.
   */
  useEffect(() => {
    if (!productDetail?.variants || productDetail.variants.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the user-overridable variant selection when the displayed product/variant catalog changes; sequencing matters for the price the buyer is charged, so this stays in an effect rather than render-derived state
      setSelectedVariantId(null);
      return;
    }
    const cheapest = [...productDetail.variants].sort((a, b) => a.price - b.price)[0];
    setSelectedVariantId(cheapest.vid);
  }, [productDetail?.id, productDetail?.variants]);

  // Derived: find the selected variant in the current product's catalog.
  const selectedVariant: ProductVariant | null =
    productDetail?.variants && selectedVariantId
      ? (productDetail.variants.find((v) => v.vid === selectedVariantId) ?? null)
      : null;

  // Sync initialCurrentList prop changes to state (for SSR hydration on page navigation)
  useEffect(() => {
    // Always sync when initialCurrentList reference changes and has data
    // This handles SSR hydration on page navigation (e.g., NavCategoryBar clicks)
    // Check sessionStorage for saved pagination - if page > 1, skip sync to preserve scroll restoration
    let savedPage = 1;
    try {
      const paginationKey = `pagination_${window.location.pathname}`;
      const saved = sessionStorage.getItem(paginationKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        savedPage = parsed.page || 1;
      }
    } catch (_e) {
      // Ignore
    }

    if (prevInitialListRef.current !== initialCurrentList && initialCurrentList.length > 0) {
      // Only preserve the existing list (skip sync) when restoring on a browser
      // back/forward navigation. On a forward navigation (e.g. a navbar click)
      // we must always replace with the new category's server list, even if a
      // stale `pagination_*` entry exists for the target URL from an earlier
      // visit — otherwise the previous category's listings stick around.
      if (savedPage > 1 && isBackNavigation()) {
        // Skip sync - restoring saved pagination on back navigation
      } else {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- synchronizes the list with a changed SSR prop only when not restoring saved pagination on back-navigation; depends on sessionStorage + navigation direction (external systems) and cannot be render-derived
        setCurrentList(initialCurrentList);
      }
    }
    prevInitialListRef.current = initialCurrentList;
  }, [initialCurrentList]);

  // One builder for every product link. This one used to interpolate whatever it
  // was handed, so a listing that had not loaded yet -- no id, empty title --
  // became `/product/undefined/`, which the router trims to /product/undefined and
  // the page then refuses to fetch (18 rows, 'watching' 11 days). The util sends
  // an id-less product home and names an untitled one.
  const getProductPath = useCallback((product: Product) => productPathFor(product), []);

  const loadOtherListings = useCallback(async () => {
    if (!productDetail || otherListLoadedRef.current) return;

    try {
      if (!productDetail.user?.id) {
        trackError('product-provider-missing-user-id', ErrorSeverity.WARNING);
        setOtherListLoaded(true);
        return;
      }

      const otherListings = await fetchOtherListings(productDetail.user.id, productDetail.id);

      // Same boundary as the recommended list above: schema-validated search
      // rows narrowed to this provider's Product view.
      setOtherList((otherListings.listings || []) as unknown as Product[]);
      setOtherListLoaded(true);
    } catch (error) {
      trackError(error, { action: 'loadOtherListings', component: 'ProductProvider' } as unknown as ErrorSeverity);
      setOtherListLoaded(true); // Mark as loaded even on error to prevent retries
    }
  }, [productDetail]);

  const loadFullProductDetails = useCallback(async (product: Product) => {
    if (!product) return;

    // Reset otherListLoaded when loading a new product
    setOtherListLoaded(false);

    try {
      // Search results now include user photos from DB batch fetch
      setProductDetail(product);

      // Ensure we have the required data
      if (!product.categoryGroupId) {
        trackError('product-provider-missing-category-group', ErrorSeverity.WARNING);
        return;
      }

      // Only load recommended listings immediately
      // Other listings will be loaded lazily when user scrolls
      // The listing's category GROUP. Sent as `categoryId` it named a
      // subcategory that does not exist, so every product page asked for an
      // empty list (3,400 zero-result searches on 2026-10-15 in served_lists).
      const categoryList = await fetchSearchList({ categoryGroupId: Number(product.categoryGroupId) });

      // fetchSearchList returns schema-validated search rows. Product is this
      // provider's own view of the same listing, and the two genuinely
      // disagree: SearchListingSchema makes `user` nullish and parses
      // `categoryId` as a number, while Product requires a user and declares
      // categoryId a string. Reconciling them is wider than this boundary --
      // seven hand-written descriptions of this row exist across the app -- so
      // the narrowing is stated here rather than hidden behind an `any` on
      // fetchSearchList's return type, where it reached every caller.
      const recommended = (categoryList as unknown as Product[]).filter((p) => p.id !== product.id);

      setRecommendedList(shuffleArray(recommended).slice(0, MAX_LIST_SIZE));

      // Note: setProduct() already handles adding product to currentList
      // Don't set it here to avoid race condition with stale closure
    } catch (error) {
      trackError(error, { action: 'loadFullProductDetails', component: 'ProductProvider' } as unknown as ErrorSeverity);
    }
  }, []);

  // useEffect to handle loading full product details after setting a product
  useEffect(() => {
    if (shouldLoadDetails) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot trigger: kicks off the async full-detail load (which internally updates product state) in response to a setProduct() signal, then clears the flag below so the effect doesn't re-fire; intentional sequencing, not render-derived state
      void loadFullProductDetails(shouldLoadDetails);
      setShouldLoadDetails(null); // Reset after loading
    }
  }, [shouldLoadDetails, loadFullProductDetails]);

  const setProduct = useCallback((product: Product, listGroup = 'currentList', _isBack = false) => {
    // Don't use changePath anymore - let the router handle navigation
    // The path change should be handled by the component that calls setProduct
    changeTitle(product.title);

    const list = listGroup === 'currentList' ? currentListRef.current :
                 listGroup === 'recommendedList' ? recommendedListRef.current :
                 listGroup === 'similarList' ? similarListRef.current :
                 otherListRef.current;

    const listIndex = list.findIndex(p => p.id === product.id);

    setProductDetail(product);
    setCurrentListGroup(listGroup);
    setCurrentListIndex(listIndex < 0 ? 0 : listIndex);

    // Only reset recommended/other lists if NOT flipping through similarList
    // This prevents triggering new API calls when using flip buttons
    if (listGroup !== 'similarList') {
      setRecommendedList(loadingList);
      setOtherList(loadingList);
    }

    // If the product isn't in the current list, add it to the beginning
    // This ensures flip buttons work when clicking from search results
    if (listIndex < 0 && listGroup === 'currentList') {
      setCurrentList([product, ...list]);
      setCurrentListIndex(0);
    }

    // Reset the overlay's own scroll, and ONLY the overlay's. Deliberately not
    // scrollProductViewToTop(): setProduct also runs when a product page mounts
    // with its SSG props, including on a browser Back into one, and forcing the
    // document to 0 there would undo the position the browser had just restored.
    // The flip buttons call the helper directly, where the reset is what the
    // reader asked for.
    // Every caller is an effect or a click handler, so this is the browser.
    const po = document.querySelector('.product-overlay') as HTMLElement;
    const pm = document.querySelector('.product-modal') as HTMLElement;
    if (po) po.scrollTop = 0;
    if (pm) pm.scrollTop = 0;

    // Handle Sellwild TV corner mode
    const sellwildTv = document.querySelector('.sellwild-wrapper') as HTMLElement;
    if (sellwildTv && typeof window !== 'undefined') {
      sellwildTv.classList.add('corner');
    }

    // Trigger loading of full product details via useEffect
    // Skip when flipping through similarList to avoid unnecessary API calls
    if (listGroup !== 'similarList') {
      setShouldLoadDetails(product);
    }
  }, []);

  /**
   * Patch one listing's status in place, after its owner changed it from the
   * product page (mark sold / delist / relist).
   *
   * Deliberately NOT setProduct(): that resets the recommended and other lists
   * and kicks off a fresh detail fetch, so a seller marking their own item sold
   * would watch the page below them blank out and reload. The status is the only
   * thing that changed, and everything the page derives from it -- isAvailable,
   * isPendingOrSold, isBuyable, the owner menu's own items -- recomputes from it.
   *
   * The lists are patched alongside productDetail because the flip buttons call
   * setProduct() with the LIST entry, not with productDetail; without this, a
   * seller who marked an item sold and flipped away and back would be handed the
   * pre-change copy.
   */
  const applyProductStatus = useCallback((listingId: string | number, status: number) => {
    const patch = (p: Product): Product =>
      String(p.id) === String(listingId) ? { ...p, status } : p;
    const patchList = (list: Product[]): Product[] =>
      list.some((p) => String(p.id) === String(listingId)) ? list.map(patch) : list;

    setProductDetail((prev) => (prev ? patch(prev) : prev));
    setCurrentList(patchList);
    setRecommendedList(patchList);
    setOtherList(patchList);
    setSimilarList(patchList);
  }, []);

  const removeProduct = useCallback(() => {
    setProductDetail(null);
    
    const sellwildTv = document.querySelector('.sellwild-wrapper') as HTMLElement;
    if (sellwildTv) {
      const rect = sellwildTv.getBoundingClientRect();
      if (
        rect.top + rect.height > 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
      ) {
        sellwildTv.classList.remove('corner');
      }
    }
  }, []);

  const flipProduct = useCallback((adjustment: number) => {
    // Prioritize similarList for flipping - this is the main navigation list
    // Fall back to other lists if similarList is empty
    let list: Product[];
    let listGroup: string;

    if (similarListRef.current.length > 0) {
      list = similarListRef.current;
      listGroup = 'similarList';
    } else if (currentListRef.current.length > 0) {
      list = currentListRef.current;
      listGroup = 'currentList';
    } else if (recommendedListRef.current.length > 0) {
      list = recommendedListRef.current;
      listGroup = 'recommendedList';
    } else if (otherListRef.current.length > 0) {
      list = otherListRef.current;
      listGroup = 'otherList';
    } else {
      return; // No list available to flip through
    }

    // Determine starting index - if switching to a different list, find current product in new list
    // or start from 0 if not found
    let startIndex = currentListIndexRef.current;
    if (listGroup !== currentListGroupRef.current) {
      // Switching lists - find current product in the new list
      const currentProductId = productDetail?.id;
      if (currentProductId) {
        const foundIndex = list.findIndex(p => p.id === currentProductId);
        startIndex = foundIndex >= 0 ? foundIndex : 0;
      } else {
        startIndex = 0;
      }
      setCurrentListGroup(listGroup);
    }

    let newIndex = startIndex + adjustment;
    if (newIndex < 0) newIndex = list.length - 1;
    else if (newIndex >= list.length) newIndex = 0;

    // newIndex is wrapped into [0, list.length) and the list is non-empty.
    const product = list[newIndex];

    // Update index before setting product
    setCurrentListIndex(newIndex);

    // Use cached product data for instant render (no API fetch needed)
    // Search results already contain all data: text, photos, dimensions, etc.
    // Update URL when flipping products — shallow to avoid triggering
    // getStaticProps which would cause a second render/product change.
    const newPath = getProductPath(product);
    if (window.location.pathname !== newPath) {
      push(newPath, { shallow: true });
    }

    setProduct(product, listGroup);

    const direction = adjustment > 0 ? 'Next' : 'Prev';
    googleEvent('prev_next_listing', { button: direction });

    // Synchronously, not on a 100ms timer: the flip swaps the content under the
    // reader immediately, so anything later is a visible jump, and the old timer
    // also raced the re-render it was waiting for. Scrolling to 0 needs no
    // settled layout -- the top of the page is valid at any content height.
    scrollProductViewToTop();
  }, [productDetail, setProduct, getProductPath, push]);

  const prevProduct = useCallback((e?: React.MouseEvent) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    flipProduct(-1);
  }, [flipProduct]);

  const nextProduct = useCallback((e?: React.MouseEvent) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    flipProduct(1);
  }, [flipProduct]);

  const getSingleProduct = useCallback(async (id: string): Promise<Product | null> => {
    try {
      const product = await fetchSearchListItem(id);
      const modal = document.querySelector('.product-modal') as HTMLElement;
      if (modal) modal.scrollTop = 0;
      return product;
    } catch (error) {
      trackError(error, { action: 'getSingleProduct', component: 'ProductProvider' } as unknown as ErrorSeverity);
      return null;
    }
  }, []);

  const reportProduct = useCallback(async (listing: Product, flag: string | number, reason: string): Promise<boolean> => {
    try {
      // Use new Next.js API endpoint instead of RPC
      const response = await fetch(`/api/listings/${listing.id}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: flag,
          reason,
        }),
      });

      const rawData = await response.json();

      // Validate response with Zod schema
      const parseResult = ReportListingResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        trackError(parseResult.error, { action: 'reportProduct validation', component: 'ProductProvider' } as unknown as ErrorSeverity);
        googleEvent('report_product_error', { productId: listing.id });
        return false;
      }

      const data = parseResult.data;

      if (response.ok && data.success) {
        googleEvent('report_product_submitted', { productId: listing.id });
        // Product reported successfully
        return true;
      } else {
        googleEvent('report_product_error', { productId: listing.id });
        trackError(!data.success ? (data as { error?: string }).error as string : 'Unknown error', { action: 'reportProduct', component: 'ProductProvider' } as unknown as ErrorSeverity);
        return false;
      }
    } catch (error) {
      trackError(error, { action: 'reportProduct', component: 'ProductProvider' } as unknown as ErrorSeverity);
      googleEvent('report_product_error', { productId: listing.id });
      return false;
    }
  }, []);

  const replaceCurrentList = useCallback((newList: Product[], searchParams?: ReplaceListSearchParams) => {
    // Check if category changed - support both categoryGroupId and categoryId
    const newCategoryId = searchParams?.categoryGroupId ?? searchParams?.categoryId ?? searchParams?.catId;
    const categoryChanged = newCategoryId !== undefined &&
                           lastCategoryIdRef.current !== null &&
                           lastCategoryIdRef.current !== newCategoryId;

    // Update the ref with the new category ID (including 0 for home page)
    if (newCategoryId !== undefined) {
      lastCategoryIdRef.current = newCategoryId;
    }

    // If category changed, always replace. Otherwise append for page > 1
    if (searchParams?.page && searchParams.page > 1 && !categoryChanged) {
      // Append to existing list, removing duplicates
      setCurrentList(prevList => {
        const existingIds = new Set(prevList.map(item => item.id));
        const uniqueNewItems = newList.filter(item => !existingIds.has(item.id));
        return [...prevList, ...uniqueNewItems];
      });
    } else {
      // Replace entire list (page 1 or category change)
      setCurrentList(newList);
    }
  }, []);

  const chooseRecommendedFromList = useCallback((productId: string, list: Product[]) => {
    const top = Math.min(list.length, RECOMMENDED_COUNT);
    const filtered = list.filter(p => p.id !== productId);
    return shuffleArray(filtered).slice(0, top);
  }, []);

  const setCoverPos = useCallback((position: number) => {
    setCoverPosition(position);
  }, []);

  const setUserListings = useCallback((listings: Product[]) => {
    setOtherList(listings);
  }, []);

  const setSimilarListings = useCallback((listings: Product[]) => {
    setSimilarList(listings);
  }, []);

  const value: ProductContextType = {
    productDetail,
    currentList,
    recommendedList,
    otherList,
    similarList,
    otherListLoaded,
    currentListGroup,
    currentListIndex,
    coverPosition,
    loaded,
    selectedVariantId,
    selectedVariant,
    setSelectedVariantId,
    setProduct,
    removeProduct,
    applyProductStatus,
    prevProduct,
    nextProduct,
    flipProduct,
    getSingleProduct,
    loadFullProductDetails,
    loadOtherListings,
    reportProduct,
    getProductPath,
    setCoverPos,
    setLoaded,
    setUserListings,
    setSimilarListings,
    replaceCurrentList,
    chooseRecommendedFromList,
  };

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

export default ProductProvider;